
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Download, RefreshCw } from "lucide-react";
import { NetworkProvider } from "@/types";
import { getAuthToken } from "@/utils/authUtils";

interface WebScrapingProps {
  onComplete: () => Promise<void>;
}

// List of providers that have scrapers implemented
const AVAILABLE_SCRAPERS: NetworkProvider[] = [
  'CBB', 'Telenor', 'Eesy', 'Oister', 'Flexii', 'Greentel'
];

// List of providers available via the Adtraction API
const API_PROVIDERS: NetworkProvider[] = [
  'Telmore', 'Duka', 'Oister', 'Eesy', 'Flexii', 'Greentel'
];

export const WebScraping = ({ onComplete }: WebScrapingProps) => {
  const { toast } = useToast();
  const [providerToScrape, setProviderToScrape] = useState<NetworkProvider>("Oister");
  const [isScrapingInProgress, setIsScrapingInProgress] = useState(false);
  const [isScrapingAll, setIsScrapingAll] = useState(false);
  const [isAdtractionFetching, setIsAdtractionFetching] = useState(false);

  const handleStartScraping = async (provider?: NetworkProvider) => {
    setIsScrapingInProgress(true);
    try {
      const token = await getAuthToken();
      
      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/scrape-offers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          provider: provider || providerToScrape
        }),
      });

      if (!response.ok) {
        throw new Error('Scraping failed');
      }

      const scrapingData = await response.json();
      toast({
        title: "Scraping completed",
        description: `Successfully scraped ${scrapingData.plansScraped} plans, saved ${scrapingData.plansSaved} plans.`,
      });
      
      await onComplete();
    } catch (error) {
      console.error('Error scraping plans:', error);
      toast({
        title: "Error scraping plans",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsScrapingInProgress(false);
    }
  };

  const handleScrapeAllProviders = async () => {
    setIsScrapingAll(true);
    
    // Only scrape providers that have scrapers implemented
    for (const provider of AVAILABLE_SCRAPERS) {
      try {
        await handleStartScraping(provider);
      } catch (error) {
        console.error(`Error scraping ${provider}:`, error);
      }
    }
    
    setIsScrapingAll(false);
  };

  const handleFetchAdtraction = async () => {
    setIsAdtractionFetching(true);
    try {
      const token = await getAuthToken();
      
      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/fetch-adtraction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('API fetch failed');
      }

      const result = await response.json();
      if (result.status === 'success') {
        toast({
          title: "Adtraction API fetch completed",
          description: `Inserted: ${result.stats.inserted}, Updated: ${result.stats.updated}, Deleted: ${result.stats.deleted}, Failed: ${result.stats.failed}, Total: ${result.stats.total}`,
        });
      } else {
        throw new Error(result.message || 'Unknown error');
      }
      
      await onComplete();
    } catch (error) {
      console.error('Error fetching from Adtraction API:', error);
      toast({
        title: "Error fetching from API",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsAdtractionFetching(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Web Scraper</CardTitle>
        <CardDescription>
          Scrape plan data automatically from provider websites or API feeds.
          This will fetch the latest plans and add them to your database.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="providerToScrape">Select Provider to Scrape</Label>
          <Select 
            value={providerToScrape} 
            onValueChange={(value: NetworkProvider) => setProviderToScrape(value)}
            disabled={isScrapingInProgress || isScrapingAll}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_SCRAPERS.map((provider) => (
                <SelectItem key={provider} value={provider}>{provider}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="bg-secondary/20 rounded-lg p-4">
          <h3 className="font-medium mb-2">Notes on Web Scraping</h3>
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>Supported providers via scraper: {AVAILABLE_SCRAPERS.join(', ')}</li>
            <li>Providers available via Adtraction API: {API_PROVIDERS.join(', ')}</li>
            <li>Scraped data might need manual review for accuracy</li>
            <li>Scraping might take several minutes to complete</li>
            <li>Provider websites may change their structure, which could cause scraping to fail</li>
            <li>The Adtraction API data is automatically updated daily at 4:00 AM</li>
          </ul>
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2">
        <Button 
          onClick={() => handleStartScraping()} 
          disabled={isScrapingInProgress || isScrapingAll || isAdtractionFetching || !providerToScrape}
          className="flex-1"
        >
          {isScrapingInProgress ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Scraping in Progress...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Start Scraping
            </>
          )}
        </Button>
        <Button
          onClick={handleScrapeAllProviders}
          disabled={isScrapingInProgress || isScrapingAll || isAdtractionFetching}
          className="flex-1"
        >
          {isScrapingAll ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Scraping All Providers...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Scrape All Providers
            </>
          )}
        </Button>
        <Button
          onClick={handleFetchAdtraction}
          disabled={isScrapingInProgress || isScrapingAll || isAdtractionFetching}
          className="w-full mt-2"
          variant="secondary"
        >
          {isAdtractionFetching ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Fetching from Adtraction API...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Fetch from Adtraction API
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
